/**
 * The left panel: every CSV column with its inferred type and sample values,
 * draggable into a zone. Each also has a menu (Input / … / Skip), so a column
 * can be placed by keyboard too. Dropping a column back here skips it.
 */

import { useState, type DragEvent } from "react";
import { GripVertical } from "lucide-react";
import { Pill } from "@/components/ui/pill";
import { Select } from "@/components/ui/select";
import type { Mapping, Target } from "../csv-mapping";
import type { ColumnProfile } from "./column-profile";
import { TARGET_OPTIONS } from "./targets";

interface Props {
  mapping: Mapping;
  profiles: ColumnProfile[];
  rows: number;
  onAssign: (index: number, target: Target) => void;
  onDragStart: (index: number, e: DragEvent) => void;
  onDropBack: () => void;
}

export function ColumnList({ mapping, profiles, rows, onAssign, onDragStart, onDropBack }: Props) {
  const [over, setOver] = useState(false);
  return (
    <section
      aria-label="CSV columns"
      className={`box flex min-w-0 flex-col ${over ? "outline-2 outline-link" : ""}`}
      onDragOver={(e) => (e.preventDefault(), setOver(true))}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => (e.preventDefault(), setOver(false), onDropBack())}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-line-soft px-4 py-3">
        <h3 className="text-sm font-semibold">CSV columns</h3>
        <span className="text-xs text-muted">{mapping.columns.length}</span>
      </div>
      <ul className="flex flex-col">
        {mapping.columns.map((c, i) => {
          const p = profiles[i];
          const samples = p?.samples ?? [];
          return (
            <li
              key={`${c.column}-${i}`}
              draggable
              onDragStart={(e) => onDragStart(i, e)}
              className={`grid cursor-grab grid-cols-[14px_minmax(0,1fr)_150px] items-center gap-2 border-b border-line-soft px-3 py-2.5 last:border-0 ${
                c.target === "skip" ? "opacity-60" : ""
              }`}
            >
              <GripVertical size={14} aria-hidden className="text-faint" />
              <span className="flex min-w-0 flex-col gap-0.5" title={samples.join("\n")}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-mono text-[13px] font-medium">{c.column}</span>
                  <Pill className="shrink-0 text-[11px]">{p?.type ?? "string"}</Pill>
                </span>
                <span className="truncate text-xs text-muted">
                  {samples.length ? samples.join(" · ") : "Empty in every row"}
                </span>
                <span className="text-[11px] text-muted">
                  {p?.filled ?? 0} of {rows} rows filled
                </span>
              </span>
              <Select
                label={`Column ${c.column}`}
                buttonClassName="h-8 text-xs"
                value={c.target}
                onChange={(t) => onAssign(i, t as Target)}
                options={TARGET_OPTIONS}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
